import { Box, Typography, Chip, Button, capitalize } from "@mui/material";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import AddIcon from '@mui/icons-material/Add';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { HourType } from "src/business/restaurant/restaurant";
import { colorTextPrimary, colorTextMuted, colorBorder, radiusInput, colorTextSuccess, colorBorderSuccess, colorSurfaceSuccess, colorSurfaceDisabled, colorBorderLight } from "src/design/tokens";
import { isDayOpen, hasEveningHours } from "../utils/scheduleUtils";
import { TimeField } from "./TimeField";
import { CustomButton } from "src/design";

interface DayScheduleCardProps {
  dayHour: HourType;
  editing: boolean;
  onChange: (updated: HourType) => void;
  onApplyToAll?: () => void;
}

export const DayScheduleCard = ({ dayHour, editing, onChange, onApplyToAll }: DayScheduleCardProps) => {
  const { t } = useTranslation();
  const open = isDayOpen(dayHour);
  const evening = hasEveningHours(dayHour);

  return (
    <Box
      sx={{
        border: `1px solid ${colorBorder}`,
        borderRadius: radiusInput,
        p: 2.5,
        mb: 2,
      }}
    >
      {/* Day header */}
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, mb: open ? 2 : 0 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colorTextPrimary }}>
          {capitalize(t(`common.days.${dayHour.day}`))}
        </Typography>
        <Chip
          label={open ? t("schedule.open") : t("schedule.closed")}
          size="small"
          variant="outlined"
          sx={{
            color: open ? colorTextSuccess : colorTextMuted,
            borderColor: open ? colorBorderSuccess : colorBorder,
            background: open ? colorSurfaceSuccess : colorSurfaceDisabled,
            fontWeight: 600,
            fontSize: "0.75rem",
          }}
        />
        {editing && (
          <Box sx={{ ml: "auto" }}>
            {open && onApplyToAll && (<Button
              size="small"
              onClick={onApplyToAll}
              sx={{ textTransform: "none", fontSize: "0.75rem", marginRight: 4 }}
              color="inherit"
              startIcon={<FileCopyOutlinedIcon sx={{ width: 16 }} />}
            >
              {t("schedule.applyToAll")}
            </Button>)}
            <Button
              size="small"
              onClick={() => {
                if (open) {
                  onChange({
                    ...dayHour,
                    morning: { start: dayjs("00:00", "HH:mm"), end: dayjs("00:00", "HH:mm") },
                    evening: null,
                  });
                } else {
                  onChange({
                    ...dayHour,
                    morning: { start: dayjs("08:00", "HH:mm"), end: dayjs("22:30", "HH:mm") },
                  });
                }
              }}
              sx={{ textTransform: "none", fontSize: "0.75rem" }}
              color={open ? "inherit" : "success"}
            >
              {open ? t("schedule.markClosed") : t("schedule.markOpen")}
            </Button>

          </Box>
        )}
      </Box>

      {open && (
        <>
          {/* Morning / main hours */}
          <Box sx={{ display: "flex", gap: 3, mb: evening || editing ? 2 : 0 }}>
            <TimeField
              label={t("schedule.openingTime")}
              value={dayHour.morning.start}
              editing={editing}
              onChange={(v) =>
                onChange({ ...dayHour, morning: { ...dayHour.morning, start: v } })
              }
              minTime={dayjs("00:00", "HH:mm")}
              maxTime={dayHour.morning.end.subtract(1, "minute")}
            />
            <TimeField
              label={t("schedule.closingTime")}
              value={dayHour.morning.end}
              editing={editing}
              onChange={(v) =>
                onChange({ ...dayHour, morning: { ...dayHour.morning, end: v } })
              }
              minTime={dayHour.morning.start.add(1, "minute")}
              allowMidnightAsEndOfDay
            />
          </Box>

          {/* Afternoon / Evening hours */}
          {evening && dayHour.evening && (
            <>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: colorTextPrimary, fontStyle: "italic" }}
                >
                  {t("schedule.afternoonEvening")}
                </Typography>
                {editing && (
                  <div style={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Button
                      size="small"
                      onClick={() => onChange({ ...dayHour, evening: null })}
                      color="error"
                      startIcon={<RemoveCircleOutlineIcon fontSize="small" sx={{ width: 16 }} />}
                      sx={{ textTransform: "none", fontSize: "0.75rem" }}
                    >

                      {t("schedule.remove")}
                    </Button>
                  </div>
                )}
              </Box>
              <Box sx={{ display: "flex", gap: 3 }}>
                <TimeField
                  label={t("schedule.openingTime")}
                  value={dayHour.evening.start}
                  editing={editing}
                  onChange={(v) =>
                    onChange({
                      ...dayHour,
                      evening: dayHour.evening
                        ? { ...dayHour.evening, start: v }
                        : { start: v, end: dayjs("00:00", "HH:mm") },
                    })
                  }
                  minTime={dayjs("00:00", "HH:mm")}
                  maxTime={dayHour.evening.end.subtract(1, "minute")}
                />
                <TimeField
                  label={t("schedule.closingTime")}
                  value={dayHour.evening.end}
                  editing={editing}
                  onChange={(v) =>
                    onChange({
                      ...dayHour,
                      evening: dayHour.evening
                        ? { ...dayHour.evening, end: v }
                        : { start: dayjs("00:00", "HH:mm"), end: v },
                    })
                  }
                  minTime={dayHour.evening.start.add(1, "minute")}
                  allowMidnightAsEndOfDay
                />
              </Box>
            </>
          )}

          {/* Add evening service button (edit mode only) */}
          {editing && !evening && (
            <CustomButton
              color='inherit'
              size="small"
              startIcon={<AddIcon color='inherit' />}
              onClick={() =>
                onChange({
                  ...dayHour,
                  evening: {
                    start: dayjs("18:00", "HH:mm"),
                    end: dayjs("23:00", "HH:mm"),
                  },
                })
              }
              variant='outlined'
              sx={{
                textTransform: "none", mt: 1, fontSize: "0.75rem", borderWidth: 1, borderRadius: 'calc(0.625rem - 2.5px)',
                borderColor: colorBorderLight,
                fontWeight: 500
              }}
            >
              {t("schedule.addAfternoonEvening")}
            </CustomButton>
          )}
        </>
      )}
    </Box>
  );
};
